// Quote Builder — Apprentice Kit: pin annotation on a plan/elevation.
//
// Second half of Apprentice Kit v1. Reasoning capture (rationale.ts) records
// WHY a line was priced as it was; this records WHERE on the drawing that line
// is, so "what's on the plan" and "what's in the list" are joined up.
//
// ⚠️ INTERNAL. A pin resolves to qb_rationale, which is commercial reasoning.
// design_files.pins_visibility exists (defaults 'internal') for the later
// fitter/customer-notes work; in v1 'shared' renders nothing customer-side, so
// there is no customer path out of here at all.
//
// ── Numbering ─────────────────────────────────────────────────────────────
// "Pin 4 = quote line 4" is a DISPLAY rule. Line order is nothing but array
// position in zone.items[], and the QB has drag-to-reorder, so the number is
// computed from the line's current position every render. The pin itself
// stores item_id. Reorder the quote and the numbers renumber themselves;
// nothing re-points at the wrong product.
//
// ── Not the shared lightbox ───────────────────────────────────────────────
// @tkos/shared-ui's lightbox is used by the customer and driver apps too.
// Placement needs click-to-place, drag-to-move and a live link to the quote
// store — pushing that into a shared viewer would put a Kit-training concern
// in a component three apps render. This is a self-contained retailer surface.

import { api } from "@tkos/api-client";
import { svgIcon } from "./formatters";
import { escapeHtml } from "./formatters";
import type { QuoteStore } from "./state";
import type { QuoteItem } from "./types";

const PIN_ICON = "M12 21s-6.5-6.1-6.5-10.5a6.5 6.5 0 1 1 13 0C18.5 14.9 12 21 12 21z";
const CLOSE_ICON = "M18 6 6 18|M6 6l12 12";

export interface PinRow {
  id: string;
  designFileId: string;
  itemId: string;
  xPct: number;
  yPct: number;
  rationale: Array<{ id: string; body: string; source: string; createdAt: string }>;
}

/** A quote line flattened with its display number — the number IS its
 *  1-based position across zones, recomputed on every open. */
interface NumberedLine {
  n: number;
  item: QuoteItem;
  zoneId: string;
  zoneName: string;
}

function numberedLines(store: QuoteStore): NumberedLine[] {
  const out: NumberedLine[] = [];
  let n = 0;
  for (const z of store.get().zones) {
    for (const item of z.items) {
      n += 1;
      out.push({ n, item, zoneId: z.id, zoneName: z.name });
    }
  }
  return out;
}

export interface PinEditorOpts {
  designFileId: string;
  imageUrl: string;
  imageName: string;
  /** Current per-design pin visibility, from design_files.pins_visibility. */
  pinsVisibility: "internal" | "shared";
  store: QuoteStore;
  showToast(msg: string, kind?: "success" | "error"): void;
  /** Called when the pin-visibility toggle changes, so the Plans panel can
   *  keep its own copy of the design row in step. */
  onVisibilityChange?(v: "internal" | "shared"): void;
}

/**
 * Open the pin editor over a drawing. Returns a close handle.
 *
 * Placement model: pick a line in the sidebar, then click the drawing. One pin
 * per line — clicking again with the same line selected moves it, which is
 * what the server's unique index enforces, so the UI cannot get out of step
 * with what the DB will accept.
 */
export function openPinEditor(opts: PinEditorOpts): { close(): void } {
  const { designFileId, imageUrl, imageName, store, showToast } = opts;
  let pins: PinRow[] = [];
  let selectedItemId: string | null = null;
  let pinsVisibility = opts.pinsVisibility;
  let saving = false;

  const overlay = document.createElement("div");
  overlay.className = "qb-pinner";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", `Pin annotation — ${imageName}`);
  overlay.tabIndex = -1;

  const close = (): void => {
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") { e.preventDefault(); close(); }
  };

  overlay.innerHTML = `
    <div class="qb-pinner__bar">
      <div class="qb-pinner__title">
        ${svgIcon(PIN_ICON)}
        <span>Pin annotation</span>
        <span class="qb-pinner__file">${escapeHtml(imageName)}</span>
      </div>
      <div class="qb-pinner__bar-right">
        <label class="qb-pinner__vis" title="Pins are Kit-training notes. 'Customer visible' is reserved for the fitter/customer notes work — it shows nothing to customers today.">
          <input type="checkbox" data-role="pins-vis" ${pinsVisibility === "shared" ? "checked" : ""} />
          <span data-role="pins-vis-label"></span>
        </label>
        <button type="button" class="qb-icon-btn" data-role="pinner-close" title="Close (Esc)" aria-label="Close">${svgIcon(CLOSE_ICON)}</button>
      </div>
    </div>
    <div class="qb-pinner__body">
      <div class="qb-pinner__stage" data-role="stage">
        <div class="qb-pinner__canvas" data-role="canvas">
          <img class="qb-pinner__img" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageName)}" draggable="false" />
          <div class="qb-pinner__pins" data-role="pins"></div>
        </div>
        <p class="qb-pinner__hint" data-role="hint"></p>
      </div>
      <aside class="qb-pinner__side">
        <h3 class="qb-pinner__side-title">Quote lines</h3>
        <p class="qb-pinner__side-hint">Pick a line, then click the drawing. The number is the line's position in the quote.</p>
        <ul class="qb-pinner__lines" data-role="lines"></ul>
      </aside>
    </div>`;

  const q = <T extends HTMLElement>(sel: string): T | null => overlay.querySelector<T>(sel);
  const pinLayer = (): HTMLElement | null => q("[data-role='pins']");
  const canvas = (): HTMLElement | null => q("[data-role='canvas']");

  function pinFor(itemId: string): PinRow | undefined {
    return pins.find((p) => p.itemId === itemId);
  }

  function renderVisibility(): void {
    const label = q("[data-role='pins-vis-label']");
    if (label) {
      label.textContent = pinsVisibility === "shared" ? "Customer visible" : "Internal only";
      label.className = pinsVisibility === "shared"
        ? "qb-pinner__vis-label qb-pinner__vis-label--shared"
        : "qb-pinner__vis-label";
    }
  }

  function renderLines(): void {
    const list = q("[data-role='lines']");
    if (!list) return;
    const lines = numberedLines(store);
    if (lines.length === 0) {
      list.innerHTML = `<li class="qb-pinner__empty">This quote has no lines yet — add items first.</li>`;
      return;
    }
    list.innerHTML = lines.map(({ n, item, zoneName }) => {
      const pin = pinFor(item.id);
      const noteCount = pin?.rationale.length ?? 0;
      const sel = selectedItemId === item.id;
      return `
        <li class="qb-pinner__line${pin ? " is-pinned" : ""}${sel ? " is-selected" : ""}"
            data-item="${escapeHtml(item.id)}">
          <button type="button" class="qb-pinner__line-btn" data-role="pick" data-item="${escapeHtml(item.id)}"
            aria-pressed="${sel ? "true" : "false"}">
            <span class="qb-pinner__num">${n}</span>
            <span class="qb-pinner__line-main">
              <span class="qb-pinner__line-desc">${escapeHtml(item.desc || item.code || "Untitled line")}</span>
              <span class="qb-pinner__line-meta">${escapeHtml(zoneName)}${item.code ? ` · ${escapeHtml(item.code)}` : ""}</span>
              ${noteCount
                ? `<span class="qb-pinner__line-note">${escapeHtml(pin!.rationale[0]!.body)}</span>`
                : `<span class="qb-pinner__line-note qb-pinner__line-note--none">No reasoning captured on this line yet</span>`}
            </span>
            <span class="qb-pinner__line-state">${pin ? "pinned" : ""}</span>
          </button>
          ${pin ? `<button type="button" class="qb-icon-btn qb-icon-btn--danger" data-role="unpin" data-item="${escapeHtml(item.id)}" title="Remove this pin">${svgIcon(CLOSE_ICON)}</button>` : ""}
        </li>`;
    }).join("");
  }

  function renderPins(): void {
    const layer = pinLayer();
    if (!layer) return;
    const lines = numberedLines(store);
    const numFor = new Map(lines.map((l) => [l.item.id, l.n]));
    layer.innerHTML = pins.map((p) => {
      // A pin whose line has been deleted keeps its position but is shown as
      // unlinked rather than renumbered onto a neighbour — see migration 0090.
      const n = numFor.get(p.itemId);
      const orphan = n === undefined;
      return `
        <button type="button"
          class="qb-pinner__pin${orphan ? " qb-pinner__pin--orphan" : ""}${selectedItemId === p.itemId ? " is-selected" : ""}"
          style="left:${p.xPct}%;top:${p.yPct}%"
          data-role="pin" data-item="${escapeHtml(p.itemId)}"
          title="${orphan ? "This pin's quote line no longer exists" : `Line ${n}`}"
          aria-label="${orphan ? "Unlinked pin" : `Pin for line ${n}`}">${orphan ? "?" : n}</button>`;
    }).join("");
  }

  function renderHint(): void {
    const hint = q("[data-role='hint']");
    if (!hint) return;
    if (!selectedItemId) {
      hint.textContent = "Pick a quote line on the right, then click the drawing to place its pin.";
      return;
    }
    const line = numberedLines(store).find((l) => l.item.id === selectedItemId);
    const existing = pinFor(selectedItemId);
    hint.textContent = existing
      ? `Click to MOVE pin ${line?.n ?? "?"} — ${line?.item.desc ?? ""}`
      : `Click the drawing to place pin ${line?.n ?? "?"} — ${line?.item.desc ?? ""}`;
  }

  function renderAll(): void {
    renderVisibility(); renderLines(); renderPins(); renderHint();
  }

  async function load(): Promise<void> {
    try {
      const res = await api<{ ok: boolean; pins: PinRow[] }>(
        `/portal/design-pins/?designFileId=${encodeURIComponent(designFileId)}`,
      );
      pins = Array.isArray(res.pins) ? res.pins : [];
    } catch {
      // Placement is assistive; an unreachable API must not leave a dead
      // overlay. Start empty and let the retailer try again.
      pins = [];
      showToast("Couldn't load existing pins", "error");
    }
    renderAll();
  }

  async function place(itemId: string, xPct: number, yPct: number): Promise<void> {
    if (saving) return;
    saving = true;
    const s = store.get();
    const line = numberedLines(store).find((l) => l.item.id === itemId);
    try {
      const res = await api<{ ok: boolean; pin: PinRow }>("/portal/design-pins/", {
        method: "POST",
        body: JSON.stringify({
          designFileId,
          itemId,
          zoneId: line?.zoneId ?? null,
          quoteId: s.serverId ?? null,
          quoteRef: s.reference ?? null,
          xPct, yPct,
        }),
      });
      if (!res.ok || !res.pin) throw new Error("save failed");
      // Replace-in-place: the server upserts, so one pin per line either way.
      pins = [...pins.filter((p) => p.itemId !== itemId), res.pin];
      renderAll();
    } catch {
      showToast("Couldn't save that pin", "error");
    } finally {
      saving = false;
    }
  }

  async function unpin(itemId: string): Promise<void> {
    const pin = pinFor(itemId);
    if (!pin) return;
    try {
      await api(`/portal/design-pins/${encodeURIComponent(pin.id)}`, { method: "DELETE" });
      pins = pins.filter((p) => p.id !== pin.id);
      renderAll();
      // Say what was NOT deleted — the reasoning is the valuable artefact and
      // removing a marker must not read as destroying the note.
      showToast("Pin removed — the reasoning is still on the line", "success");
    } catch {
      showToast("Couldn't remove that pin", "error");
    }
  }

  // ── Events ───────────────────────────────────────────────────────────────
  overlay.addEventListener("click", (e) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;

    if (t.closest("[data-role='pinner-close']")) { close(); return; }
    // Click on the backdrop (never on the canvas or sidebar) closes.
    if (t === overlay) { close(); return; }

    const pick = t.closest<HTMLElement>("[data-role='pick']");
    if (pick) {
      const id = pick.dataset["item"] ?? "";
      selectedItemId = selectedItemId === id ? null : id;
      renderAll();
      return;
    }
    const un = t.closest<HTMLElement>("[data-role='unpin']");
    if (un) { void unpin(un.dataset["item"] ?? ""); return; }

    const marker = t.closest<HTMLElement>("[data-role='pin']");
    if (marker) {
      // Selecting via the marker is the natural way to re-position it.
      selectedItemId = marker.dataset["item"] ?? null;
      renderAll();
      return;
    }
  });

  // Placement: percentage of the RENDERED image box, which is what makes the
  // stored value resolution-independent (migration 0090).
  const stageClick = (e: MouseEvent): void => {
    const img = q<HTMLImageElement>(".qb-pinner__img");
    const c = canvas();
    if (!img || !c) return;
    if ((e.target as HTMLElement).closest("[data-role='pin']")) return;
    if (!selectedItemId) {
      showToast("Pick a quote line first", "error");
      return;
    }
    const box = img.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return;
    const xPct = ((e.clientX - box.left) / box.width) * 100;
    const yPct = ((e.clientY - box.top) / box.height) * 100;
    if (xPct < 0 || xPct > 100 || yPct < 0 || yPct > 100) return;
    void place(selectedItemId, Math.round(xPct * 1000) / 1000, Math.round(yPct * 1000) / 1000);
  };
  overlay.addEventListener("click", (e) => {
    const c = canvas();
    if (c && (e.target === c || (e.target as HTMLElement).classList.contains("qb-pinner__img")
      || (e.target as HTMLElement).dataset["role"] === "pins")) {
      stageClick(e as MouseEvent);
    }
  });

  overlay.addEventListener("change", (e) => {
    const cb = (e.target as HTMLElement)?.closest<HTMLInputElement>("[data-role='pins-vis']");
    if (!cb) return;
    const next: "internal" | "shared" = cb.checked ? "shared" : "internal";
    void (async () => {
      try {
        await api(`/portal/portal-designs/${encodeURIComponent(designFileId)}`, {
          method: "PATCH",
          body: JSON.stringify({ pinsVisibility: next }),
        });
        pinsVisibility = next;
        opts.onVisibilityChange?.(next);
        renderVisibility();
        showToast(next === "shared"
          // Honest about the v1 behaviour rather than implying it published
          // something. See the header.
          ? "Pins marked customer-visible — nothing is shown to customers yet"
          : "Pins set to internal only", "success");
      } catch {
        cb.checked = pinsVisibility === "shared";
        showToast("Couldn't change pin visibility", "error");
      }
    })();
  });

  document.addEventListener("keydown", onKey);
  document.body.appendChild(overlay);
  overlay.focus();
  renderAll();
  void load();

  return { close };
}
